import { FormattedMessage } from "react-intl";
import styled from "styled-components";
import { TEmployeeOccupiedScheduleItem } from "../../../../../api/employee/apiGetEmployeeOccupiedSchedule";
import { cuideoTheme } from "../../../../../containers/themes/defaultTheme";
import {
  DAYS,
  OCCUPIED_BACKGROUND,
  OCCUPIED_BORDER_COLOR,
  Schedule,
  TimeRange,
  formatOccupiedBlockLabel,
} from "../../../../jobsite/components/WeeklyCalendar";
import { StyledLabel } from "../CoordinatorFileSC";

const StyledLegend = styled.div`
  margin-bottom: 10px;
`;

const StyledLegendItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 2px 0;
  font-size: 0.8rem;
  color: ${cuideoTheme.palette.primary.main};
`;

const StyledSwatch = styled.span`
  flex-shrink: 0;
  width: 12px;
  height: 12px;
  margin-top: 2px;
  border: 1px solid ${OCCUPIED_BORDER_COLOR};
  border-radius: 2px;
  background-image: ${OCCUPIED_BACKGROUND};
`;

const StyledHint = styled.div`
  font-size: 0.75rem;
  font-style: italic;
  color: ${cuideoTheme.palette.grey[600]};
`;

const sameRanges = (a: TimeRange[] = [], b: TimeRange[] = []): boolean =>
  a.length === b.length &&
  a.every((range, idx) => range[0] === b[idx][0] && range[1] === b[idx][1]);

// Resume el horario agrupando los días consecutivos con las mismas franjas,
// p. ej. «lun–vie 00:00-24:00, sáb 00:00-09:00, dom 21:00-24:00».
export const summarizeOccupiedSchedule = (schedule: Schedule): string => {
  const parts: string[] = [];
  const dayLabel = (idx: number) => DAYS[idx].label.toLowerCase();
  let i = 0;
  while (i < DAYS.length) {
    const ranges = schedule[DAYS[i].key] || [];
    if (!ranges.length) {
      i++;
      continue;
    }
    let j = i;
    while (j + 1 < DAYS.length && sameRanges(schedule[DAYS[j + 1].key], ranges)) {
      j++;
    }
    const days = j === i ? dayLabel(i) : `${dayLabel(i)}–${dayLabel(j)}`;
    const hours = ranges.map(([start, end]) => `${start}-${end}`).join(" / ");
    parts.push(`${days} ${hours}`);
    i = j + 1;
  }
  return parts.join(", ");
};

interface IOccupiedBlocksLegendProps {
  blocks: TEmployeeOccupiedScheduleItem[];
}

/**
 * Leyenda de los horarios ocupados por los contratos de la candidata (ECO-1499).
 * Se muestra aunque no haya calendario de «Horario preferido» (internas).
 */
const OccupiedBlocksLegend = ({ blocks }: IOccupiedBlocksLegendProps) => {
  if (!blocks.length) return null;

  return (
    <StyledLegend data-testid="occupied-blocks-legend">
      <StyledLabel>
        <FormattedMessage
          id="CoordinatorFile.OccupiedBlocks.Title"
          defaultMessage="Horario ocupado por contratos"
        />
      </StyledLabel>
      {blocks.map((block) => (
        <StyledLegendItem key={block.contractId}>
          <StyledSwatch />
          <span>
            <strong>{formatOccupiedBlockLabel(block)}</strong>
            {block.estado ? ` (${block.estado})` : ""}
            {`: ${summarizeOccupiedSchedule(block.schedule)}`}
          </span>
        </StyledLegendItem>
      ))}
      <StyledHint>
        <FormattedMessage
          id="CoordinatorFile.OccupiedBlocks.Hint"
          defaultMessage="Se calcula a partir de sus contratos seleccionados o activos y no se puede editar ni eliminar desde aquí."
        />
      </StyledHint>
    </StyledLegend>
  );
};

export default OccupiedBlocksLegend;
